import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/api';
import { Spinner, showToast } from '../components/UI';
import {
  AppShell, TopNav, NavDrawer, BodyGrid, PageBody, useDrawer,
} from '../components/Layout';

const AGENT_COLORS = { ceo:'#0e0e0d', builder:'#378add', content:'#5dcaa5', marketing:'#c8563a', research:'#e8a87c' };
const STATUS_META  = {
  pending:      { label:'Pending',      bg:'var(--paper-3)',          text:'var(--ink-mute)' },
  in_progress:  { label:'Running',      bg:'var(--ink)',              text:'var(--paper)'    },
  needs_review: { label:'Needs Review', bg:'rgba(232,168,124,0.2)',   text:'#9a6030'         },
  done:         { label:'Done',         bg:'rgba(93,202,165,0.15)',   text:'#2d8a6e'         },
  blocked:      { label:'Blocked',      bg:'rgba(224,112,112,0.15)', text:'#c03030'         },
};
const EVENT_META = {
  task_started:        { icon:'▶',  label:'Task started',     color:'#378add' },
  task_done:           { icon:'✓',  label:'Task completed',   color:'#5dcaa5' },
  task_blocked:        { icon:'✗',  label:'Task blocked',     color:'#e07070' },
  guidance_received:   { icon:'💬', label:'Guidance added',   color:'#c8563a' },
  guidance_edit_log:   { icon:'✏',  label:'Guidance edited',  color:'#e8a87c' },
  guidance_delete_log: { icon:'🗑', label:'Guidance deleted', color:'#aaa'   },
  agent_output:        { icon:'🤖', label:'Agent output',     color:'#378add' },
};

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return new Date(ts).toLocaleDateString();
}

// Shared shell wrapper to avoid repeating TopNav/NavDrawer
function Shell({ drawer, breadcrumb, children }) {
  return (
    <AppShell>
      <TopNav
        breadcrumb={breadcrumb}
        drawerOpen={drawer.open}
        onToggleDrawer={drawer.toggle}
        drawerPinned={drawer.pinned}
      />
      <NavDrawer
        open={drawer.open}
        onClose={drawer.close}
        pinned={drawer.pinned}
        onTogglePin={drawer.togglePin}
      />
      {children}
    </AppShell>
  );
}

export default function TaskDetailPage() {
  const { taskId }  = useParams();
  const [params]    = useSearchParams();
  const workspaceId = params.get('workspace_id');
  const navigate    = useNavigate();
  const drawer      = useDrawer();

  const [task, setTask]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [running, setRunning] = useState(false);

  const [guidanceText, setGuidanceText]     = useState('');
  const [guidanceSaving, setGuidanceSaving] = useState(false);
  const [editingId, setEditingId]           = useState(null);
  const [editText, setEditText]             = useState('');

  async function loadTask() {
    if (!workspaceId || !taskId) return;
    try {
      const data = await apiFetch(`/api/workspaces/${workspaceId}/tasks/${taskId}`);
      setTask(data);
    } catch (e) {
      setError(e.message || 'Failed to load task.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadTask(); }, [taskId, workspaceId]);

  async function handleRunTask() {
    setRunning(true);
    try {
      navigate(`/dashboard/workspaces/${workspaceId}/tasks/${taskId}/output`);
    } finally {
      setRunning(false);
    }
  }

  async function handleAddGuidance(e) {
    e.preventDefault();
    if (!guidanceText.trim()) return;
    setGuidanceSaving(true);
    try {
      await apiFetch(`/api/workspaces/${workspaceId}/tasks/${taskId}/guidance`, {
        method:'POST', body: JSON.stringify({ guidance_text: guidanceText.trim() }),
      });
      setGuidanceText('');
      showToast('Guidance added.');
      await loadTask();
    } catch { showToast('Failed to save guidance.', 'error'); }
    finally { setGuidanceSaving(false); }
  }

  async function handleEditGuidance(eventId) {
    if (!editText.trim()) return;
    try {
      await apiFetch(`/api/workspaces/${workspaceId}/tasks/${taskId}/guidance/${eventId}`, {
        method:'PATCH', body: JSON.stringify({ guidance_text: editText.trim() }),
      });
      setEditingId(null);
      showToast('Guidance updated.');
      await loadTask();
    } catch { showToast('Failed to update guidance.', 'error'); }
  }

  async function handleDeleteGuidance(eventId) {
    if (!window.confirm('Delete this guidance?')) return;
    try {
      await apiFetch(`/api/workspaces/${workspaceId}/tasks/${taskId}/guidance/${eventId}`, { method:'DELETE' });
      showToast('Guidance deleted.');
      await loadTask();
    } catch { showToast('Failed to delete guidance.', 'error'); }
  }

  if (loading) return (
    <Shell drawer={drawer} breadcrumb="Task">
      <BodyGrid drawerPinned={drawer.pinned}>
        <div style={{ display:'flex', flex:1, alignItems:'center', justifyContent:'center' }}>
          <Spinner size={32} />
        </div>
      </BodyGrid>
    </Shell>
  );

  if (error || !task) return (
    <Shell drawer={drawer} breadcrumb="Task">
      <BodyGrid drawerPinned={drawer.pinned}>
        <div style={{ display:'flex', flex:1, alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'12px' }}>
          <p style={{ fontSize:'14px', color:'var(--ink-mute)' }}>{error || 'Task not found.'}</p>
          <button onClick={() => navigate(-1)} style={s.cancelBtn}>← Go back</button>
        </div>
      </BodyGrid>
    </Shell>
  );

  const sm         = STATUS_META[task.status] || { label:task.status, bg:'var(--paper-3)', text:'var(--ink-mute)' };
  const agentColor = AGENT_COLORS[task.agent_type] || '#888';
  const isFailed   = task.status === 'blocked' || !!task.error_message;
  const isDone     = task.status === 'done';

  const activeGuidance = (task.events||[]).filter(
    e => e.event_type === 'guidance_received' && !e.payload?.data?.deleted
  );
  const systemEvents = (task.events||[]).filter(
    e => !['guidance_received','guidance_edit_log','guidance_delete_log'].includes(e.event_type)
  );

  return (
    <Shell drawer={drawer} breadcrumb={task.title}>
      <BodyGrid drawerPinned={drawer.pinned}>
        <PageBody style={{ maxWidth:'none' }}>
          <div style={{ maxWidth:'800px' }}>

            {/* ── Header ── */}
            <div style={{ marginBottom:'24px' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'16px', marginBottom:'12px', flexWrap:'wrap' }}>
                <h1 style={{ fontFamily:'var(--serif)', fontSize:'26px', fontWeight:'400', lineHeight:1.25, flex:1 }}>
                  {task.title}
                </h1>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0, marginTop:'4px' }}>
                  <span style={{ fontSize:'12px', fontWeight:'500', padding:'4px 12px', borderRadius:'10px',
                    background:sm.bg, color:sm.text }}>
                    {sm.label}
                  </span>
                  <button
                    onClick={handleRunTask}
                    disabled={running}
                    style={{ ...s.runBtn, opacity: running ? 0.7 : 1, cursor: running ? 'not-allowed' : 'pointer' }}>
                    {isDone ? 'View Output' : 'Task Not Completed Yet'}
                  </button>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', fontSize:'12px',
                  fontWeight:'500', padding:'3px 10px', borderRadius:'10px',
                  background:`${agentColor}18`, color:agentColor }}>
                  <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:agentColor }} />
                  {task.agent_type}
                </span>
                {task.priority <= 2 && (
                  <span style={{ fontSize:'11px', fontWeight:'600', color:'var(--accent)',
                    background:'rgba(200,86,58,0.08)', padding:'2px 8px', borderRadius:'4px' }}>
                    Priority {task.priority}
                  </span>
                )}
                <span style={{ fontSize:'12px', color:'var(--ink-mute)' }}>by {task.created_by}</span>
                {task.run_date && (
                  <span style={{ fontSize:'12px', color:'var(--ink-mute)' }}>
                    Run: {new Date(task.run_date).toLocaleDateString()}
                  </span>
                )}
                <span style={{ fontSize:'12px', color:'var(--ink-mute)' }}>{timeAgo(task.created_at)}</span>
              </div>
            </div>

            {/* ── Success banner ── */}
            {isDone && !isFailed && (
              <div style={{ background:'rgba(93,202,165,0.1)', border:'1px solid rgba(93,202,165,0.3)',
                borderRadius:'var(--r)', padding:'12px 16px', marginBottom:'20px',
                display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px', flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <span style={{ fontSize:'18px' }}>✓</span>
                  <div>
                    <strong style={{ fontSize:'13px', color:'#2d8a6e', display:'block' }}>Task completed successfully</strong>
                    <span style={{ fontSize:'12px', color:'#2d8a6e', opacity:0.8 }}>
                      {new Date(task.updated_at).toLocaleString()}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/dashboard/workspaces/${workspaceId}/tasks/${taskId}/output`}
                  style={{ fontSize:'12px', color:'#2d8a6e', fontWeight:'500', textDecoration:'none',
                    padding:'6px 14px', border:'1px solid rgba(93,202,165,0.4)', borderRadius:'6px',
                    background:'rgba(93,202,165,0.1)', flexShrink:0 }}>
                  View output →
                </Link>
              </div>
            )}

            {/* ── Failure banner ── */}
            {isFailed && (
              <div style={{ background:'rgba(224,112,112,0.08)', border:'1px solid rgba(224,112,112,0.3)',
                borderRadius:'var(--r)', padding:'12px 16px', marginBottom:'20px' }}>
                <strong style={{ fontSize:'13px', color:'#c03030', display:'block', marginBottom:'4px' }}>
                  ⚠ Task failed or blocked
                </strong>
                {task.error_message && (
                  <pre style={{ fontSize:'12px', color:'#c03030', margin:0, whiteSpace:'pre-wrap',
                    fontFamily:'monospace', lineHeight:1.5 }}>
                    {task.error_message}
                  </pre>
                )}
              </div>
            )}

            {/* ── Artifacts ── */}
            {task.artifacts?.length > 0 && (
              <section style={s.section}>
                <h2 style={s.sTitle}>📎 Artifacts produced</h2>
                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                  {task.artifacts.map(a => (
                    <Link key={a.id}

                    to={
                  a.type === 'campaign'
                    ? `/dashboard/campaigns/${a.id}`
                    : `/dashboard/artifacts/${a.id}`
                    } style={{
                      display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px',
                      borderRadius:'var(--r)', background:'var(--white)',
                      border:'1px solid var(--paper-3)', textDecoration:'none', color:'var(--ink)',
                    }}
                      onMouseEnter={e => e.currentTarget.style.borderColor='var(--accent)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor='var(--paper-3)'}>
                      <span style={{ fontSize:'20px' }}>{a.type==='site'?'🌐':a.type==='research'?'🔍':'📄'}</span>
                      <div style={{ flex:1 }}>
                        <strong style={{ fontSize:'13px', display:'block' }}>{a.title}</strong>
                        <span style={{ fontSize:'11px', color:'var(--ink-mute)' }}>
                          {a.type} · v{a.version} · {timeAgo(a.created_at)}
                        </span>
                      </div>
                      <span style={{ fontSize:'12px', color:'var(--accent)' }}>View →</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* ── Description ── */}
            {task.description && (
              <section style={s.section}>
                <h2 style={s.sTitle}>Description</h2>
                <div style={{ fontSize:'14px', color:'var(--ink-soft)', lineHeight:1.7,
                  background:'var(--white)', border:'1px solid var(--paper-3)',
                  borderRadius:'var(--r)', padding:'16px 18px', whiteSpace:'pre-wrap' }}>
                  {task.description}
                </div>
              </section>
            )}

            {/* ── Guidance ── */}
            <section style={s.section}>
              <h2 style={s.sTitle}>
                💬 Guidance to agent
                <span style={{ fontSize:'12px', fontWeight:'400', color:'var(--ink-mute)', marginLeft:'8px' }}>
                  ({activeGuidance.length})
                </span>
              </h2>
              <p style={{ fontSize:'13px', color:'var(--ink-mute)', marginBottom:'14px', lineHeight:1.5 }}>
                Instructions injected into the agent's prompt before it runs.
              </p>

              {activeGuidance.map(ev => {
                const text = ev.payload?.data?.guidance_text || '';
                const ts   = ev.payload?.timestamp || ev.created_at;
                const isEditing = editingId === ev.id;
                return (
                  <div key={ev.id} style={{ background:'rgba(200,86,58,0.04)',
                    border:'1px solid rgba(200,86,58,0.15)', borderRadius:'var(--r)',
                    padding:'14px 16px', marginBottom:'10px' }}>
                    {isEditing ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                        <textarea value={editText} onChange={e => setEditText(e.target.value)}
                          style={{ ...s.textarea, minHeight:'80px' }} autoFocus />
                        <div style={{ display:'flex', gap:'8px' }}>
                          <button onClick={() => handleEditGuidance(ev.id)} style={s.saveBtn}>Save</button>
                          <button onClick={() => setEditingId(null)} style={s.cancelBtn}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p style={{ fontSize:'13px', color:'var(--ink)', lineHeight:1.6,
                          marginBottom:'8px', whiteSpace:'pre-wrap' }}>{text}</p>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <span style={{ fontSize:'11px', color:'var(--ink-mute)' }}>
                            {ev.payload?.actor || 'You'} · {timeAgo(ts)}
                          </span>
                          <div style={{ display:'flex', gap:'8px' }}>
                            <button onClick={() => { setEditingId(ev.id); setEditText(text); }} style={s.iconBtn}>Edit</button>
                            <button onClick={() => handleDeleteGuidance(ev.id)}
                              style={{ ...s.iconBtn, color:'#e07070' }}>Delete</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              <form onSubmit={handleAddGuidance} style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                <textarea value={guidanceText} onChange={e => setGuidanceText(e.target.value)}
                  placeholder="Add optional guidance for the agent — e.g. 'Focus on Toronto market only'…"
                  style={{ ...s.textarea, minHeight:'90px' }} disabled={guidanceSaving} />
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                  <button type="submit" disabled={guidanceSaving || !guidanceText.trim()}
                    style={{ ...s.saveBtn, opacity: guidanceSaving || !guidanceText.trim() ? 0.4 : 1 }}>
                    {guidanceSaving ? 'Saving…' : 'Add guidance'}
                  </button>
                </div>
              </form>
            </section>

            {/* ── Event log ── */}
            {systemEvents.length > 0 && (
              <section style={s.section}>
                <h2 style={s.sTitle}>Event log</h2>
                {systemEvents.map((ev, idx) => {
                  const meta = EVENT_META[ev.event_type] || { icon:'•', label:ev.event_type, color:'var(--ink-mute)' };
                  const payload = ev.payload || {};
                  return (
                    <div key={ev.id} style={{ display:'flex', gap:'12px', paddingBottom:'16px', position:'relative' }}>
                      {idx < systemEvents.length - 1 && (
                        <div style={{ position:'absolute', left:'15px', top:'28px', bottom:0,
                          width:'1px', background:'var(--paper-3)' }} />
                      )}
                      <div style={{ width:'30px', height:'30px', borderRadius:'50%', flexShrink:0, zIndex:1,
                        background:`${meta.color}18`, border:`1px solid ${meta.color}40`,
                        display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px' }}>
                        {meta.icon}
                      </div>
                      <div style={{ flex:1, paddingTop:'4px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'2px' }}>
                          <span style={{ fontSize:'13px', fontWeight:'500', color:meta.color }}>
                            {payload.message || meta.label}
                          </span>
                          <span style={{ fontSize:'11px', color:'var(--ink-mute)' }}>{timeAgo(ev.created_at)}</span>
                        </div>
                        {payload.data && Object.keys(payload.data).length > 0 && (
                          <pre style={{ fontSize:'11px', color:'var(--ink-mute)', margin:0, fontFamily:'monospace',
                            lineHeight:1.6, background:'var(--paper-2)', padding:'8px 10px', borderRadius:'6px',
                            whiteSpace:'pre-wrap', maxHeight:'120px', overflowY:'auto' }}>
                            {JSON.stringify(payload.data, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  );
                })}
              </section>
            )}

          </div>
        </PageBody>
      </BodyGrid>
    </Shell>
  );
}

const s = {
  section:   { background:'var(--white)', border:'1px solid var(--paper-3)',
               borderRadius:'var(--r)', padding:'20px 24px', marginBottom:'16px' },
  sTitle:    { fontFamily:'var(--sans)', fontSize:'14px', fontWeight:'600',
               color:'var(--ink)', marginBottom:'12px' },
  textarea:  { width:'100%', padding:'12px 14px', fontSize:'13px', lineHeight:1.6,
               fontFamily:'var(--sans)', border:'1px solid var(--paper-3)', borderRadius:'8px',
               background:'var(--paper)', color:'var(--ink)', resize:'vertical',
               outline:'none', boxSizing:'border-box', transition:'border-color 0.2s' },
  runBtn:    { padding:'8px 16px', background:'var(--ink)', color:'#fff', border:'none',
               borderRadius:'6px', fontSize:'13px', fontWeight:'500',
               fontFamily:'var(--sans)', display:'flex', alignItems:'center', gap:'6px' },
  saveBtn:   { padding:'8px 18px', background:'var(--ink)', color:'#fff', border:'none',
               borderRadius:'6px', fontSize:'13px', fontWeight:'500', cursor:'pointer', fontFamily:'var(--sans)' },
  cancelBtn: { padding:'8px 14px', background:'none', color:'var(--ink-mute)',
               border:'1px solid var(--paper-3)', borderRadius:'6px', fontSize:'13px',
               cursor:'pointer', fontFamily:'var(--sans)' },
  iconBtn:   { background:'none', border:'none', fontSize:'12px', cursor:'pointer',
               color:'var(--ink-mute)', fontFamily:'var(--sans)', padding:'2px 6px', borderRadius:'4px' },
};